import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import CompositeEventPopupMock from '../compositeEvent/CompositeEventPopupMock';
import CompositeEventScene from '../compositeEvent/CompositeEventScene';
import { useSafetyCompositeEvent } from '../useSafetyPageData';

/**
 * ### CompositeEventSection
 *
 * Explains how the product decides a danger situation from several
 * detections rather than from one sound: a staged photograph of the
 * situation on one side and, beside it, the monitoring system's own
 * composite-event pop-up replayed with demo data, inside the same kind of
 * screen frame the monitoring section uses. Cause on the left, judgment on
 * the right. The three points under it say how the combination is set up
 * and what it removes.
 *
 * @returns The rendered composite-event section element
 * @see {@link CompositeEventScene} The photograph of the situation
 * @see {@link CompositeEventPopupMock} The replayed pop-up
 * @see {@link useSafetyCompositeEvent} The copy rendered here
 */
export default function CompositeEventSection() {
  const { t } = useTranslation('common');
  const composite = useSafetyCompositeEvent(t);

  return (
    <section data-analytics-section="composite_events">
      <Area>
        <SectionTitle
          color="lighter"
          title={t('ListenAI Safety는 상황을 판단합니다')}
          description={t(
            '위험 상황은 하나의 소리로만 판단되지 않습니다. 현장에 맞는 조건을 설정해 여러 감지 신호를 하나의 상황으로 묶고, 실제 확인이 필요한 상황만 정확하게 알립니다.',
          )}
        />

        <div className="mt-10 lg:mt-[clamp(40px,5vw,60px)]">
          <AniSlideUp>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-stretch lg:gap-[clamp(24px,2.5vw,40px)]">
              <CompositeEventScene />
              <CompositeEventPopupMock />
            </div>
          </AniSlideUp>
          <p className="mt-3 text-[13px] font-medium leading-[150%] tracking-[-0.26px] text-apps-brandGray600 lg:text-[14px]">
            {t(
              '사진은 연출된 장면이고, 팝업은 모니터링 시스템의 복합 이벤트 알림을 시연 데이터로 재현한 화면이에요. 타임라인의 소리를 직접 들어볼 수 있어요.',
            )}
          </p>
        </div>
        <ul className="mt-8 flex flex-col gap-[20px] lg:mt-[clamp(32px,4.167vw,56px)] lg:grid lg:grid-cols-3 lg:gap-8">
          {composite.points.map((point, index) => (
            <AniSlideUp key={point.title} delay={0.15 * index} element="li">
              <div className="h-full border-t border-t-apps-brandGray50 pt-[20px]">
                <h3 className="text-[18px] font-bold leading-[140%] tracking-[-0.36px] text-apps-coreGrayWhite lg:text-[20px] lg:tracking-[-0.4px]">
                  {point.title}
                </h3>
                <p className="mt-2 text-[15px] font-medium leading-[150%] tracking-[-0.3px] text-apps-brandGray600 lg:text-[17px] lg:leading-[160%]">
                  {point.description}
                </p>
              </div>
            </AniSlideUp>
          ))}
        </ul>
      </Area>
    </section>
  );
}
